import { keepPreviousData, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import {
  Organization,
  OrganizationUsersResponse,
  USER_STATE_IN_ORGANIZATION,
  UserInvitation,
  UserOrganizationAccess,
  UserOrganizationAccessAndInvites,
  archiveNotification,
  deleteCompletedActions,
  deleteReadUserNotifications,
  deleteUserNotification,
  getInboxNotifications,
  getOrganization,
  getOrganizationUsers,
  getUserInvitationRequests,
  getUserNotifications,
  getUserOrganizationAccess,
  getUserOrganizationAccessAndInvites,
  markAllActionsDone,
  markAllActionsUndone,
  markReadAllUnreadUserNotification,
  markUnreadAllReadUserNotification,
  readUserNotification,
  removeSpaceFromInvitation,
  setNotificationDone,
  setNotificationUndone,
  unarchiveNotification,
  unreadUserNotification,
  updateOrganization,
  updateOrganizationMetadata,
  updateSpaceRoleInInvitation,
} from '../api/organization';
import { getCurrentlyActiveOrganization, OrganizationAccessRoles, useGetUserFromAccessToken } from '../common/auth';
import { useEffect, useState } from 'react';
import { queryClient } from '../queryClient';
import { WorkspaceAccessRole } from '../types/WorkspaceAccess';
import { QUERY_CONFIG } from '../constants/queryConfig';
import { Notification, NotificationFilter, NotificationTab } from '../types/Notification';
import { queryKeys } from './queryKeys';

export const useGetOrganization = (organizationId: number) => {
  return useQuery<Organization, Error>({
    queryKey: ['organization', organizationId],
    queryFn: async () => (await getOrganization(organizationId)).data,
    enabled: !!organizationId,
    staleTime: QUERY_CONFIG.STALE_TIME,
  });
};

export const useGetOrganizationUsers = () => {
  const [org, setorg] = useState<Organization | null>(null);

  useEffect(() => {
    const getCurrentOrg = async () => {
      const currentOrg = await getCurrentlyActiveOrganization();
      setorg(currentOrg);
    };

    getCurrentOrg();
  }, []);

  return useQuery<OrganizationUsersResponse[], Error>({
    queryKey: ['organization', org?.id, 'users'],
    queryFn: async () => {
      return (await getOrganizationUsers(org.id)).data;
    },
    enabled: !!org?.id,
    staleTime: QUERY_CONFIG.STALE_TIME,
  });
};

export const useGetOrganizationAccessesWithOrganization = () => {
  return useQuery<UserOrganizationAccess, Error>({
    queryKey: ['usersOrganizationAccess'],
    queryFn: async () => {
      return (await getUserOrganizationAccess()).data;
    },
    staleTime: QUERY_CONFIG.STALE_TIME,
  });
};

/**
 * Organizations this user actively administers (ADMIN + ACTIVE), for any screen that
 * needs a plain "which organization" choice without re-deriving the filter itself.
 *
 * `usersStateInOrganization` DEFAULTS to INVITED for an admin who has not yet activated,
 * and the server's organizationGuard requires ACTIVE - offering anything else here is a
 * choice that is guaranteed to come back 403 (see GithubSetupComplete, which applies the
 * same filter inline before this was factored out for SlackSetupComplete).
 */
export const useGetOrganizations = () => {
  const me = useGetUserFromAccessToken();
  const access = useQuery<UserOrganizationAccess[], Error>({
    queryKey: ['usersOrganizationAccess'],
    queryFn: async () => (await getUserOrganizationAccess()).data,
    staleTime: QUERY_CONFIG.STALE_TIME,
  });

  const userId = me.data?.user?.id;
  const data =
    access.data && userId
      ? access.data.filter((org) =>
          org.OrganizationAccess?.some(
            (orgAccess) =>
              orgAccess.userId === userId &&
              orgAccess.roleToAssume === OrganizationAccessRoles.ADMIN &&
              orgAccess.usersStateInOrganization === USER_STATE_IN_ORGANIZATION.ACTIVE
          )
        )
      : undefined;

  return {
    data,
    isLoading: access.isLoading || me.isLoading,
    isError: access.isError || me.isError,
  };
};

export const useGetOrganizationAccessesAndInvitesWithOrganization = () => {
  return useQuery<UserOrganizationAccessAndInvites, Error>({
    queryKey: ['usersOrganizationAccessAndInvites'],
    queryFn: async () => {
      return (await getUserOrganizationAccessAndInvites()).data;
    },
    staleTime: QUERY_CONFIG.STALE_TIME,
  });
};

export const useUpdateOrganizationMutation = () => {
  return useMutation({
    mutationFn: async ({ orgId, updatedFields }: { orgId: number; updatedFields: Partial<Organization> }) => {
      return (await updateOrganization(orgId, updatedFields)).data;
    },
    onSuccess: async (data) => {
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: ['organization', parseInt(data?.organizationWithAccess.id)],
        });
      }, 400);
    },
  });
};

export const useGetOrganizationInvites = () => {
  const [org, setorg] = useState<Organization | null>(null);

  useEffect(() => {
    const getCurrentOrg = async () => {
      const currentOrg = await getCurrentlyActiveOrganization();
      setorg(currentOrg);
    };

    getCurrentOrg();
  }, []);

  return useQuery<UserInvitation[], Error>({
    queryKey: ['organizationInvites', org?.id],
    queryFn: async () => (await getUserInvitationRequests(org.id)).data,
    enabled: !!org?.id,
    staleTime: QUERY_CONFIG.STALE_TIME,
  });
};

export const useRemoveSpaceAccessFromInvitationMutation = (organizationId: number) => {
  return useMutation({
    mutationFn: async ({ invitationId, spaceId }: { invitationId: number; spaceId: number }) => {
      return await removeSpaceFromInvitation(invitationId, organizationId, spaceId);
    },
    onSuccess: () => {
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: ['organizationInvites', organizationId],
        });
      }, 300);
    },
  });
};

export const useUpdateSpaceRoleInInvitationMutation = (organizationId: number) => {
  return useMutation({
    mutationFn: async ({
      invitationId,
      spaceId,
      newRole,
    }: {
      invitationId: number;
      spaceId: number;
      newRole: WorkspaceAccessRole;
    }) => {
      return await updateSpaceRoleInInvitation(invitationId, organizationId, spaceId, newRole);
    },
    onSuccess: () => {
      setTimeout(() => {
        queryClient.invalidateQueries({
          queryKey: ['organizationInvites', organizationId],
        });
      }, 300);
    },
  });
};

export const useGetUserNotifications = (organizationId: number) => {
  return useQuery<Notification[], Error>({
    queryKey: ['notifications', organizationId],
    queryFn: async () => (await getUserNotifications(organizationId)).data,
    enabled: !!organizationId,
    staleTime: QUERY_CONFIG.STALE_TIME,
  });
};

/**
 * What the sidebar's Inbox badge counts: the notifications still asking for
 * something. The two tabs track different state, so a single `read` count is
 * wrong — Actions are never marked read (opening one must not clear it), so
 * counting `!read` there left completed actions in the badge forever (#3003).
 * Mirrors the server's getNotificationCounts: open actions + unread updates.
 */
export const countUnresolvedNotifications = (notifications: Notification[] = []): number =>
  notifications.reduce((total, n) => total + ((n.requiresAction ? !n.done : !n.read) ? 1 : 0), 0);

/** The single source for the sidebar Inbox badge — used by every sidebar host. */
export const useUnresolvedNotificationsCount = (organizationId: number): number => {
  const { data } = useGetUserNotifications(organizationId);
  return countUnresolvedNotifications(data);
};

export const useInboxNotifications = (organizationId: number, tab: NotificationTab, filter: NotificationFilter) => {
  return useInfiniteQuery({
    queryKey: queryKeys.inbox(organizationId, tab, filter),
    queryFn: async ({ pageParam }) =>
      (
        await getInboxNotifications(organizationId, {
          tab,
          // 'all' is a client-only pseudo-filter: omit it so the server's "no filter" default (all rows) applies.
          ...(filter !== 'all' && { filter }),
          limit: 30,
          cursor: pageParam,
        })
      ).data,
    initialPageParam: undefined as number | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: !!organizationId,
    // Keep the previous tab's data (incl. counts) visible while the newly-selected
    // tab's query loads, so the tab row doesn't flash to zero counts and re-center (#2869).
    placeholderData: keepPreviousData,
  });
};

type InboxAction = 'read' | 'unread' | 'done' | 'undone' | 'archive' | 'unarchive' | 'delete';

export const useNotificationActionMutation = (organizationId: number) => {
  return useMutation({
    mutationFn: async ({ id, action }: { id: number; action: InboxAction }) => {
      switch (action) {
        case 'read':
          return (await readUserNotification(organizationId, id)).data;
        case 'unread':
          return (await unreadUserNotification(organizationId, id)).data;
        case 'done':
          return (await setNotificationDone(organizationId, id)).data;
        case 'undone':
          return (await setNotificationUndone(organizationId, id)).data;
        case 'archive':
          return (await archiveNotification(organizationId, id)).data;
        case 'unarchive':
          return (await unarchiveNotification(organizationId, id)).data;
        case 'delete':
          return (await deleteUserNotification(organizationId, id)).data;
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['inbox', organizationId] });
      // The sidebar unread badge reads a separate query (#2742).
      void queryClient.invalidateQueries({ queryKey: ['notifications', organizationId] });
    },
  });
};

type InboxBulkAction =
  | 'markAllRead'
  | 'markAllUnread'
  | 'markAllDone'
  | 'markAllUndone'
  | 'deleteAllRead'
  | 'deleteAllCompleted';

export const useInboxBulkMutation = (organizationId: number) => {
  return useMutation({
    mutationFn: async ({ action }: { action: InboxBulkAction }) => {
      switch (action) {
        case 'markAllRead':
          return (await markReadAllUnreadUserNotification(organizationId)).data;
        case 'markAllUnread':
          return (await markUnreadAllReadUserNotification(organizationId)).data;
        case 'markAllDone':
          return (await markAllActionsDone(organizationId)).data;
        case 'markAllUndone':
          return (await markAllActionsUndone(organizationId)).data;
        case 'deleteAllRead':
          return (await deleteReadUserNotifications(organizationId)).data;
        case 'deleteAllCompleted':
          return (await deleteCompletedActions(organizationId)).data;
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['inbox', organizationId] });
      // The sidebar unread badge reads a separate query (#2742).
      void queryClient.invalidateQueries({ queryKey: ['notifications', organizationId] });
    },
  });
};
